package com.siriusxm.cmc.utils

import kotlinx.datetime.*
import kotlin.js.Date
import kotlin.time.Duration
import kotlin.time.Duration.Companion.hours
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Duration.Companion.seconds
import kotlin.time.Instant

/**
 * Get a standard h:mm a formatted time string.
 */
public fun LocalDateTime.toAMPMTimeString(
    includeSeconds: Boolean = false, includeAmPm: Boolean = true,
    includeMinutes: Boolean = true,
    includeMilliseconds: Boolean = false
): String {
    return time.toAMPMTimeString(includeSeconds, includeAmPm, includeMinutes, includeMilliseconds)
}

/**
 * Get a standard h:mm a formatted time string.
 *
 * When [includeMilliseconds] is true the seconds segment is forced on and the
 * millisecond component is appended to it, producing "h:mm:ss.SSS".
 */
public fun LocalTime.toAMPMTimeString(
    includeSeconds: Boolean = false, includeAmPm: Boolean = true,
    includeMinutes: Boolean = true, includeMilliseconds: Boolean = false
): String {
    val isPM = this.hour >= 12
    val hour = this.hour.let {
        if (it == 0) {
            12
        } else if (it > 12) {
            it - 12
        } else {
            it
        }
    }.toString()
    val minutes = minute.toString().padStart(2, '0')

    val seconds =
        this.second.toString().padStart(2, '0')

    // Milliseconds imply seconds; the ms are appended to the seconds segment as ss.SSS.
    val showSeconds = includeSeconds || includeMilliseconds
    val secondsSegment = if (includeMilliseconds) {
        val millis = (nanosecond / 1_000_000).toString().padStart(3, '0')
        "$seconds.$millis"
    } else {
        seconds
    }

    val time = buildList {
        add(hour)
        if (includeMinutes) {
            add(minutes)
            if (showSeconds) {
                add(secondsSegment)
            }
        }
    }.joinToString(":")

    return if (includeAmPm) {
        "$time ${if (isPM) "pm" else "am"}"
    } else {
        time
    }
}

/**
 * Return the instant as a string with the date and time in the system local time.
 */
public fun Instant.toLocalDateTimeString(
    includeSeconds: Boolean = false,
    includeMilliseconds: Boolean = false
): String {
    return toLocalDateTime().toLocalDateTimeString(includeSeconds, includeMilliseconds)
}

public fun LocalDateTime.toLocalDateTimeString(
    includeSeconds: Boolean = false,
    includeMilliseconds: Boolean = false
): String {
    return "$date ${toAMPMTimeString(includeSeconds, includeMilliseconds = includeMilliseconds)}"
}

/**
 * Return the instant as a compact date/time string using month/day and time, in the system local
 * time. The year is only included if it differs from the current year.
 * Example: "Mar 19 2:30 pm" or "Dec 5 2025 2:30 pm"
 *
 * To render in a specific (e.g. user-selected) zone, project the instant with
 * `Instant.toLocalDateTimeIn(zoneId)` first and call the [LocalDateTime] overload below.
 */
public fun Instant.toShortDateTimeString(includeSeconds: Boolean = false): String {
    return toLocalDateTime().toShortDateTimeString(includeSeconds)
}

/**
 * Compact month/day/time formatting for an already-projected [LocalDateTime]. The year is only
 * included when it differs from the current (system-local) year.
 */
public fun LocalDateTime.toShortDateTimeString(includeSeconds: Boolean = false): String {
    val currentYear = kotlin.time.Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).year
    val month = this.month.toString().camelcase().take(3)
    val day = this.dayOfMonth
    val time = this.toAMPMTimeString(includeSeconds)
    return if (this.year == currentYear) {
        "$month $day $time"
    } else {
        "$month $day ${this.year} $time"
    }
}

/**
 * Return the instant as a string with the day of week, month day and year  and time in the system local time.
 */
public fun Instant.toLocalExtendedDateTimeString(includeSeconds: Boolean = false): String {
    return toLocalDateTime().let {
        "${it.dayOfWeek.name.camelcase()} ${it.month.toString().camelcase()} ${it.day} ${
            it.toAMPMTimeString(
                includeSeconds
            )
        }"
    }
}

public fun Instant.toLocalDayVerbose(): String {
    return toLocalDateTime().date.toLocalDayVerbose()
}

public fun LocalDate.toLocalDayVerbose(): String {
    return "${this.dayOfWeek.name.camelcase()} ${this.month.toString().camelcase()} ${this.day}, ${this.year}"
}

/**
 * Get a local date as Monday, Jun 23
 */
public fun LocalDate.toLocalDayString(): String {
    return "${this.dayOfWeek.name.camelcase()} ${this.month.toString().camelcase().take(3)} ${this.day}"
}

public fun String.camelcase(): String {
    return this.lowercase().replaceFirstChar { it.uppercase() }
}

/**
 * Get the local date/time in the system timezone
 */
public fun Instant.toLocalDateTime(): LocalDateTime {
    return toLocalDateTime(TimeZone.currentSystemDefault())
}

/**
 * Get a human-readable duration string that looks like "3 hours, 23 minutes, 14 seconds"
 */
public fun Duration.toDurationHumanString(includeMilliseconds: Boolean = false): String {
    val str = arrayListOf<String>()
    val d = this
    if (d.inWholeHours > 0) {
        str += "${d.inWholeHours} hours"
    }
    val withoutHours = (d - d.inWholeHours.hours)
    if (withoutHours.inWholeMinutes > 0) {
        str += "${withoutHours.inWholeMinutes} minutes"
    }
    val withoutMinutes = withoutHours - withoutHours.inWholeMinutes.minutes
    if (withoutMinutes.inWholeSeconds > 0) {
        str += "${withoutMinutes.inWholeSeconds} seconds"
    }

    if (includeMilliseconds) {
        val withoutSeconds = withoutMinutes - withoutMinutes.inWholeSeconds.seconds
        if (withoutSeconds.inWholeMilliseconds > 0) {
            str += "${withoutSeconds.inWholeMilliseconds} ms"
        }
    }

    if (str.isEmpty()) {
        return "0 ms"
    }

    return str.joinToString(", ")
}

/**
 * Get a compact duration string like "1h 13m", "23m 14s", or "< 1m".
 * Shows hours and minutes for durations >= 1 hour, minutes and seconds otherwise.
 */
public fun Duration.toCompactDurationString(): String {
    val h = inWholeHours
    val m = (this - h.hours).inWholeMinutes
    val s = (this - h.hours - m.minutes).inWholeSeconds
    return when {
        h > 0 -> "${h}h ${m}m"
        m > 0 -> "${m}m ${s}s"
        s > 0 -> "${s}s"
        else -> "< 1s"
    }
}

public fun getTimezoneUTCOffset(timezone: String): TimeZone {
    val date = Date()

    // Get the UTC timestamp

    val localeOptions = js("{}")
    localeOptions.timeZone = "UTC"

    val utcDate = Date(date.toLocaleString("en-US", localeOptions))

    localeOptions.timeZone = timezone
    console.dir(localeOptions)
    val offsetDate = Date(date.toLocaleString("en-US", localeOptions))

    val offsetTime = offsetDate.getTime()
    val utcTime = utcDate.getTime()

    val offset = offsetTime - utcTime
    val offsetMinutes = (offset / 1000.0 / 60).toInt()
    val offsetHours = offsetMinutes / 60

    println("Hours: " + offsetDate.getHours())
    println("Hours UTC: " + utcDate.getHours())
    println("offsetMs: " + offset)
    println("offsetMinutes: " + offsetMinutes)
    println("offsetHours: " + offsetHours)

    val tz = "${offsetHours}00"

    return FixedOffsetTimeZone(UtcOffset(offsetHours))
}
